import React from 'react';
import { useCrmPipelineSummary } from '../hooks';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Skeleton } from '../../../components/ui/Skeleton';
import { DataView } from '../../../components/ui/DataView';

interface CrmPipelineSummaryWidgetProps {
  orgUnitId?: string;
  from?: string;
  to?: string;
}

export function CrmPipelineSummaryWidget({ orgUnitId, from, to }: CrmPipelineSummaryWidgetProps) {
  const query = useCrmPipelineSummary({ orgUnitId, from, to });

  return (
    <Card className="overflow-hidden h-full flex flex-col justify-between">
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold tracking-wide uppercase text-text-muted">
          CRM Funnel & Conversion
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DataView
          query={query}
          isEmpty={() => false}
          loadingSkeleton={
            <div className="space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
            </div>
          }
        >
          {(summary) => {
            const prospects = summary?.prospectCount ?? 0;
            const leads = summary?.leadCount ?? 0;
            const converted = summary?.convertedThisMonth ?? 0;
            const lost = summary?.lostThisMonth ?? 0;

            const stages = [
              {
                name: 'Prospects',
                count: prospects,
                color: 'bg-primary-500',
                percentage: prospects > 0 ? 100 : 0,
              },
              {
                name: 'Leads',
                count: leads,
                color: 'bg-warning-500',
                percentage: prospects > 0 ? Math.round((leads / prospects) * 100) : 0,
              },
              {
                name: 'Converted',
                count: summary?.funnelStages?.find((f) => f.stage === 'CONVERTED')?.count ?? converted,
                color: 'bg-success-500',
                percentage: leads > 0 ? Math.round(((summary?.funnelStages?.find((f) => f.stage === 'CONVERTED')?.count ?? converted) / leads) * 100) : 0,
              },
            ];

            return (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3.5 bg-surface-2 rounded-lg border border-border">
                    <span className="text-caption text-text-muted uppercase tracking-wider block">Total Prospects</span>
                    <span className="text-2xl font-bold font-inter tnum text-text-primary">{prospects}</span>
                  </div>
                  <div className="p-3.5 bg-surface-2 rounded-lg border border-border">
                    <span className="text-caption text-text-muted uppercase tracking-wider block">Active Leads</span>
                    <span className="text-2xl font-bold font-inter tnum text-text-primary">{leads}</span>
                  </div>
                  <div className="p-3.5 bg-surface-2 rounded-lg border border-border">
                    <span className="text-caption text-text-muted uppercase tracking-wider block">Converted (Month)</span>
                    <span className="text-2xl font-bold font-inter tnum text-success-600">{converted}</span>
                  </div>
                  <div className="p-3.5 bg-surface-2 rounded-lg border border-border">
                    <span className="text-caption text-text-muted uppercase tracking-wider block">Lost (Month)</span>
                    <span className="text-2xl font-bold font-inter tnum text-danger-600">{lost}</span>
                  </div>
                </div>

                <div className="space-y-5">
                  {stages.map((stage, i) => (
                    <div key={stage.name} className="space-y-1.5">
                      <div className="flex justify-between items-end text-sm">
                        <span className="font-semibold text-text-primary flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${stage.color}`} />
                          {stage.name}
                        </span>
                        <span className="text-text-muted font-medium">
                          <strong className="text-text-primary font-bold font-inter text-base tnum mr-1">{stage.count}</strong>
                          {i > 0 && <span className="text-caption">({stage.percentage}% of previous)</span>}
                        </span>
                      </div>
                      <div className="h-3 w-full bg-surface-2 rounded-full overflow-hidden border border-border/40">
                        <div
                          className={`h-full ${stage.color} rounded-full transition-all duration-500`}
                          style={{ width: `${i === 0 && prospects > 0 ? 100 : Math.min(100, stage.percentage)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-text-secondary">
                  <span>Overall Conversion Rate (Prospect &rarr; Converted):</span>
                  <span className="font-semibold text-success-600">
                    {prospects > 0
                      ? `${Math.round(((summary?.funnelStages?.find((f) => f.stage === 'CONVERTED')?.count ?? converted) / prospects) * 100)}%`
                      : '0%'}
                  </span>
                </div>
              </>
            );
          }}
        </DataView>
      </CardContent>
    </Card>
  );
}

